import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Grid, Stack, Typography } from '@mui/material';
import { loadFinancialSupportProgramIntoWizard, readFinancialSupportPrograms } from './financialSupportProgramWizardStorage';
import { getFinancialSupportProgramsCatalog } from './financialSupportProgramsSeed';
import {
  detailFieldSx,
  detailLabelSx,
  detailPageCardSx as pageCardSx,
  detailSectionTitleSx,
  detailValueSx,
} from '../../styles/detailStyles';
import { accordionExpandIconSx } from '../../styles/iconStyles';

function DetailField({ label, value, fullWidth = false, minHeight }) {
  return (
    <Grid size={{ xs: 12, md: fullWidth ? 12 : 6 }}>
      <Box sx={{ ...detailFieldSx, minHeight: minHeight || detailFieldSx.minHeight }}>
        <Typography sx={detailLabelSx}>
          {label}
        </Typography>
        <Typography sx={detailValueSx}>
          {value || '-'}
        </Typography>
      </Box>
    </Grid>
  );
}

function formatApplicability(isApplicable, value, suffix = '') {
  if (!isApplicable) return 'Not applicable';
  if (value === null || value === undefined || value === '') return '-';
  return `${value}${suffix}`;
}

export default function FinancialSupportView() {
  const { id } = useParams();
  const navigate = useNavigate();

  const program = useMemo(() => {
    const programs = getFinancialSupportProgramsCatalog(readFinancialSupportPrograms());
    return programs.find((item) => String(item.id) === String(id));
  }, [id]);

  const handleEdit = () => {
    if (!program) return;
    loadFinancialSupportProgramIntoWizard(program);
    navigate('/financialSupport/create-program');
  };

  if (!program) {
    return (
      <Box sx={{ width: '100%', maxWidth: { xs: '100%', lg: 1480, xl: 1400 }, boxSizing: 'border-box', mx: 'auto', px: { xs: 0, lg: 3, xl: 4 } }}>
        <Button
          onClick={() => navigate('/financialSupport')}
          startIcon={<span className="material-symbols-rounded">arrow_back_ios_new</span>}
          sx={{ minWidth: 'auto', px: 0, mb: 3, color: '#1C1B1F', textTransform: 'none', fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', fontWeight: 500 }}
        >
          Back
        </Button>
        <Box sx={pageCardSx}>
          <Typography sx={{ fontSize: 20, lineHeight: '28px', fontWeight: 700, color: '#1C1B1F', mb: 1.5 }}>
            Program not found
          </Typography>
          <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>
            This financial support program could not be found.
          </Typography>
        </Box>
      </Box>
    );
  }

  const basicInfo = program.basicInfo || {};
  const contributionRules = program.contributionRules || {};
  const plans = Array.isArray(program.plans) ? program.plans : [];

  return (
    <Box sx={{ width: '100%', maxWidth: { xs: '100%', lg: 1480, xl: 1400 }, boxSizing: 'border-box', mx: 'auto', px: { xs: 0, lg: 3, xl: 4 } }}>
      <Grid container alignItems="center" mb={4} rowSpacing={2} columnSpacing={2}>
        <Grid size={{ xs: 12, md: 3 }} textAlign={{ xs: 'left', md: 'left' }}>
          <Button
            onClick={() => navigate('/financialSupport')}
            startIcon={<span className="material-symbols-rounded">arrow_back_ios_new</span>}
            sx={{
              minWidth: 'auto',
              px: 0,
              color: '#1C1B1F',
              textTransform: 'none',
              fontSize: 16,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              fontWeight: 500,
              '& .material-symbols-rounded': { fontSize: 18 },
            }}
          >
            Back
          </Button>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }} order={{ xs: 3, md: 2 }}>
          <Typography
            className="app-page-title"
            sx={{
              textAlign: 'center',
              fontSize: { xs: 28, sm: 30, md: 34 },
              lineHeight: { xs: '34px', sm: '36px', md: '40px' },
              letterSpacing: '-0.25px',
              fontWeight: 700,
              color: '#1C1B1F',
            }}
          >
            Financial Support Program Details
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 3 }} order={{ xs: 2, md: 3 }} textAlign={{ xs: 'left', md: 'right' }}>
          <Button
            onClick={handleEdit}
            startIcon={<span className="material-symbols-rounded">edit</span>}
            sx={{
              minWidth: { xs: '100%', sm: 86 },
              height: 40,
              borderRadius: '12px',
              backgroundColor: '#DFE5F6',
              color: '#294190',
              textTransform: 'none',
              fontSize: 16,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              fontWeight: 500,
              '&:hover': { backgroundColor: '#DFE5F6' },
              '& .material-symbols-rounded': { fontSize: 18 },
            }}
          >
            Edit
          </Button>
        </Grid>
      </Grid>

      <Stack spacing={3}>
        <Box sx={pageCardSx}>
          <Typography sx={detailSectionTitleSx}>
            Basic Info
          </Typography>
          <Grid container spacing={2.25}>
            <DetailField label="Program Name" value={basicInfo.programName || program.programName} />
            <DetailField label="Support Type" value={basicInfo.supportType || program.type} />
            <DetailField label="Service Provider" value={basicInfo.serviceProvider || program.provider} />
            <DetailField label="Region" value={basicInfo.region || program.region} />
            <DetailField label="Currency" value={basicInfo.currency} />
            <DetailField label="Validity Range" value={program.validity} />
            <DetailField label="Program Status" value={program.status === 'ACTIVE' ? 'Active' : program.status === 'INACTIVE' ? 'Inactive' : program.status} />
            <DetailField label="Last Updated" value={program.lastUpdated} />
            <DetailField label="Description" value={basicInfo.description} fullWidth minHeight={120} />
          </Grid>
        </Box>

        <Box sx={pageCardSx}>
          <Typography sx={detailSectionTitleSx}>
            Contribution Rules
          </Typography>
          <Grid container spacing={2.25}>
            <DetailField label="Setup Fee Contribution Required" value={contributionRules.setupFeeContributionRequired ? 'Yes' : 'No'} />
            <DetailField label="Setup Fee Percentage" value={`${contributionRules.setupFeePercentage ?? 0}%`} />
            <DetailField label="Subscription Fee Contribution Required" value={contributionRules.subscriptionFeeContributionRequired ? 'Yes' : 'No'} />
            <DetailField label="Subscription Fee Percentage" value={`${contributionRules.subscriptionFeePercentage ?? 0}%`} />
          </Grid>
        </Box>

        <Box sx={pageCardSx}>
          <Typography sx={detailSectionTitleSx}>
            Configured Plans
          </Typography>

          {plans.length ? (
            <Stack spacing={1.5}>
              {plans.map((plan, index) => (
                <Accordion
                  key={plan.id || index}
                  disableGutters
                  sx={{
                    border: '1px solid #DFE5F6',
                    borderRadius: '16px !important',
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<Box component="span" className="material-symbols-rounded" sx={accordionExpandIconSx}>expand_more</Box>}
                    sx={{ px: 2.5, py: 1.25 }}
                  >
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ width: '100%', pr: 1 }}>
                      <Typography sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 700, color: '#1C1B1F' }}>
                        Plan {index + 1}: {plan.planName || '-'}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip label={plan.supportType || '-'} size="small" sx={{ bgcolor: '#EEF3FF', color: '#294190', fontWeight: 600 }} />
                        <Chip label={plan.currency || '-'} size="small" sx={{ bgcolor: '#F1F1F1', color: '#1C1B1F' }} />
                        <Chip label={plan.planStatus || '-'} size="small" sx={{ bgcolor: '#EEF8DE', color: '#4F8506', fontWeight: 600 }} />
                      </Stack>
                      <Typography sx={{ ml: { md: 'auto' }, fontSize: 15, lineHeight: '22px', color: '#1C1B1F', fontWeight: 600 }}>
                        Amount: {plan.currency || ''} {plan.planAmount ?? '-'}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0.5 }}>
                    <Grid container spacing={2.25}>
                      <DetailField label="Description / Notes" value={plan.description} />
                      <DetailField label="Interest Rate" value={formatApplicability(plan.interestRateApplicable, plan.interestRate, '%')} />
                      <DetailField label="Repayment Duration" value={formatApplicability(plan.repaymentDurationApplicable, plan.repaymentDuration, ' months')} />
                      <DetailField label="Number of Installments" value={formatApplicability(plan.numberOfInstallmentsApplicable, plan.numberOfInstallments)} />
                      <DetailField label="Payment Frequency" value={plan.paymentFrequencyApplicable ? (plan.paymentFrequency || '-') : 'Not applicable'} />
                      <DetailField label="Grace Period" value={formatApplicability(plan.gracePeriodApplicable, plan.gracePeriod, ' days')} />
                      <DetailField label="Processing Fee Amount" value={formatApplicability(plan.processingFeeAmountApplicable, plan.processingFeeAmount ? `${plan.currency || ''} ${plan.processingFeeAmount}` : plan.processingFeeAmount)} />
                      <DetailField label="Late Payment Fee Amount" value={formatApplicability(plan.latePaymentFeeAmountApplicable, plan.latePaymentFeeAmount ? `${plan.currency || ''} ${plan.latePaymentFeeAmount}` : plan.latePaymentFeeAmount)} />
                      <DetailField label="Collateral Required" value={plan.collateralRequiredApplicable ? (plan.collateralRequired || '-') : 'Not applicable'} />
                      <DetailField label="Collateral Details" value={plan.collateralRequiredApplicable && plan.collateralRequired === 'Yes' ? plan.collateralDetails : 'Not applicable'} />
                      <DetailField label="Maximum Coverage Percentage" value={formatApplicability(plan.maximumCoveragePercentageApplicable, plan.maximumCoveragePercentage, '%')} />
                      <DetailField label="Matching Contribution Required" value={plan.matchingContributionRequiredApplicable ? (plan.matchingContributionRequired || '-') : 'Not applicable'} />
                      <DetailField label="Matching Contribution Percentage" value={plan.matchingContributionRequiredApplicable && plan.matchingContributionRequired === 'Yes' ? formatApplicability(true, plan.matchingContributionPercentage, '%') : 'Not applicable'} />
                      <DetailField label="Disbursement Schedule" value={plan.disbursementScheduleApplicable ? (plan.disbursementSchedule || '-') : 'Not applicable'} />
                      <DetailField label="Custom Schedule Explanation" value={plan.disbursementScheduleApplicable && plan.disbursementSchedule === 'Custom' ? plan.customDisbursementSchedule : 'Not applicable'} />
                      <DetailField label="Equity / Revenue Share" value={formatApplicability(plan.equityRevenueShareApplicable, plan.equityRevenueShare, '%')} />
                      <DetailField label="Expected Return Rate" value={formatApplicability(plan.expectedReturnRateApplicable, plan.expectedReturnRate, '%')} />
                      <DetailField label="Exit / Vesting Period" value={formatApplicability(plan.exitVestingPeriodApplicable, plan.exitVestingPeriod, ' months')} />
                      <DetailField label="Eligibility Criteria" value={plan.eligibilityCriteria} fullWidth />
                      <DetailField label="Disbursement Conditions" value={plan.disbursementConditions} fullWidth />
                      <DetailField label="Repayment Notes / Terms" value={plan.repaymentNotes} fullWidth />
                      <DetailField label="Other Conditions" value={plan.otherConditions} fullWidth />
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          ) : (
            <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#6B6880' }}>
              No plans were configured for this program.
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
